import EmberObject from '@ember/object';
import Validator from 'validator';
const {isURL} = Validator;
import Striptags from 'striptags';
import url from 'url';
const {parse, format} = url;
import ENV from 'sh-www/config/environment';
import md5 from 'blueimp-md5';

/**
 * Given a string, convert all '<br>' html tags into new lines.
 *
 * @param {String} stringToConvert
 */
export function convertBRsToNewLines(stringToConvert) {
  return stringToConvert ? stringToConvert.replace(/<br>/g, '\n') : stringToConvert;
}

/**
 * Given a string, convert all new lines into '<br>' html tags.
 *
 * @param {String} stringToConvert
 */
export function convertNewLinesToBRs(stringToConvert) {
  return stringToConvert ? stringToConvert.replace(/\n/g, '<br>') : stringToConvert;
}

/**
 * Given a string, escape all characters that would break a RegExp when interpolated
 *
 * @param {String} search
 * @return {String}
 */
export function escapeRegExp(search) {
  return search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Given a URI object, format it and return a string.
 *
 * @param {Object} uriObject    URI Object as defined by the URL package @see https://www.npmjs.com/package/url
 * @return {String}             Well-formed URI.
 */
export function formatUri(uriObject) {
  return format(uriObject);
}

/**
 * Given an ember object, get a string that describes the component.
 *
 * @param emberObject
 * @return {string}
 */
export function getLoggerNameForEmberObject(emberObject) {
  let objectDescription = emberObject.toString(),
    nameParts = objectDescription.split(':');

  if (nameParts.length >= 2) {
    return nameParts[1];
  }

  return `unknown-logger/${objectDescription}`;
}

/**
 * Get the model name for the ember model instance.
 *
 * @param {DS.Model.*} modelInstance
 * @return {null|String}
 */
export function getModelNameForEmberModelInstance(modelInstance) {
  if (!modelInstance || !modelInstance.get) {
    return null;
  }

  return modelInstance.get('constructor.modelName');
}

/**
 * True if matches a valid email format.
 *
 * @param {String} email
 * @return {boolean}
 */
export function isEmail(email) {
  let regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(String(email).toLowerCase());
}

/**
 * True if we are in the local dev environment. e.g. storyhunterlocal.com ONLY.
 *
 * @return {boolean}
 */
export function isInDevMode() {
  return /storyhunterlocal/.test(ENV.topLevelDomain);
}

/**
 * Quick utility function to generate nested strings.  Give
 * it a comma-separated list of strings and it'll return
 * them concatenated, joined with a '.'
 *
 * @param routePartials {string}
 * @returns {string}
 */
export function nestPartials(...routePartials) {
  return routePartials.join('.');
}

/**
 * @param {String} urlToCheck   True if this is a valid url
 */
export function isUrl(urlToCheck) {
  return isURL(urlToCheck);
}

/**
 * Accepts a value (probably should be a string if you care about thing)
 * and checks to see if it's an empty string
 */
export function isBlank(string) {
  return (string === '');
}

/**
 * For the given value and object, determine if the value is in the object and return TRUE immediately if
 * this value is found.
 *
 * @param {*} value         Value of object, should be a literal, will not do deep compares.
 * @param {Object} object   Object to iterate through and test the values for.
 * @return {boolean}        True if the value is in the object.
 */
export function isValueInObject(value, object) {
  for (let key in object) {
    if (!object.hasOwnProperty(key)) {
      continue;
    }

    if (value === object[key]) {
      return true;
    }
  }

  return false;
}

/**
 * Takes a URI-encoded JSON-string and attempts to convert it to an Ember object.
 * Returns false if unable to convert
 *
 * @param encodedJsonString {string}
 * @returns {Ember.Object | false}
 */
export function decodeUriJson(encodedJsonString) {
  if (encodedJsonString == null) {
    return false;
  }

  let returnedJson;
  try {
    returnedJson = EmberObject.create(JSON.parse(decodeURIComponent(encodedJsonString)));
  } catch (exception) {
    returnedJson = false;
  }
  return returnedJson;
}


/**
 * Takes a JSON object and converts it into a uri-encoded string for use in a URI/Query String
 *
 * @param jsonObject
 * @returns {string}
 */
export function encodeUriJson(jsonObject) {
  if (jsonObject == null) {
    return false;
  }
  return encodeURIComponent(JSON.stringify(jsonObject));
}

/**
 * Given a string, encode all html entities
 *
 * @param {String} str
 * @return {string}
 */
export function htmlEntities(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Decode all html entities
 *
 * @param {String} str
 * @return {string}
 */
export function htmlEntitiesDecode(str) {
  return String(str).replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"');
}

/**
 * Parse a URI into easily inspected parts.
 *
 * @param uriToParse
 * @return {Object}     URI Object as defined by the 'ur' package @see https://www.npmjs.com/package/url
 */
export function parseUri(uriToParse) {
  return parse(uriToParse, true);
}

/**
 * Wrapper for md5Hashing function defined on the window.
 *
 * @param {String} stringToHash
 * @return {String}
 */
export function md5Hash(stringToHash) {
  return md5(stringToHash);
}

/**
 * Given a string, strip all HTML tags from it.
 *
 * @param {String} stringToStripFrom            The string you'll want to strip from
 * @param {Array<String>=} opt_allowableTags    An array of allowed tags.  e.g. ['a', 'li', 'div']
 *
 * @return {String|*} Return a string with tags stripped.  If value is falsey, just return that back.
 */
export function stripTags(stringToStripFrom, opt_allowableTags) {
  if (!stringToStripFrom) {
    return stringToStripFrom;
  }

  return Striptags(stringToStripFrom, opt_allowableTags ? opt_allowableTags : []);
}
